import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

export enum CounterStyle {
  Default = 'default',
  Primary = 'primary'
}

@Component({
  selector: 'app-counter',
  templateUrl: './counter.component.html',
  styleUrl: './counter.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CounterComponent),
      multi: true
    }
  ],
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CounterComponent implements ControlValueAccessor {
  removable: InputSignal<boolean> = input(false);
  remove: OutputEmitterRef<boolean> = output<boolean>();

  min: InputSignal<number> = input(1);
  max: InputSignal<number> = input(999);

  // Style
  style: InputSignal<CounterStyle> = input<CounterStyle>(CounterStyle.Default);

  // State
  value: WritableSignal<number> = signal<number>(1);
  isDisabled: WritableSignal<boolean> = signal<boolean>(false);

  onInput(event: Event): void {
    const eventTarget = event.target as HTMLInputElement;
    let newValue: number = Number(eventTarget.value);

    eventTarget.value = newValue.toString();

    this.value.set(newValue);
    this.onChange?.(newValue);
  }

  test(event: Event) {
    const eventTarget = event.target as HTMLInputElement;
    let newValue: number = Number(eventTarget.value);

    if (newValue < this.min()) {
      newValue = this.min();
    } else if (newValue > this.max()) {
      newValue = this.max();
    }

    eventTarget.value = newValue.toString();

    this.value.set(newValue);
    this.onChange?.(newValue);
  }

  onDecrementClick() {
    if (this.value() <= this.min()) {
      if (this.removable()) {
        this.remove.emit(true);
      }

      return;
    }

    const updatedValue: number = this.value() - 1;
    this.value.set(updatedValue);
    this.onChange?.(updatedValue);
  }

  onIncrementClick() {
    if (this.value() >= this.max()) {
      return;
    }

    const updatedValue: number = this.value() + 1;
    this.value.set(updatedValue);
    this.onChange?.(updatedValue);
  }

  // ControlValueAccessor
  private onChange?: (value: number) => void;
  onTouch?: () => void;

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  writeValue(outsideValue: any): void {
    this.value.set(outsideValue);
  }
}
